import {getAccessToken} from '../helpers/storage';
import {updateNotificationToken} from './notificationApi';
import {
    buildDeviceMetadata,
    deleteFcmToken,
    getFcmToken,
    getNotificationPermissionStatus,
    loadStoredPushNotificationState,
    persistPushNotificationState,
    PUSH_PERMISSION_STATUS,
    requestAndroidNotificationPermission,
} from './pushNotifications';

const buildSyncResult = ({ success, message, notificationToken = '', syncedAt = new Date().toISOString() }) => ({
    success,
    message,
    notificationToken,
    syncedAt,
});

const buildTokenPayload = async (notificationToken) => {
    const metadata = await buildDeviceMetadata();

    return {
        notificationToken,
        ...metadata,
    };
};

const persistSuccessfulSync = async ({ notificationToken, isEnabled, message }) => {
    await persistPushNotificationState({
        isEnabled,
        tokenRegistered: Boolean(notificationToken),
        lastToken: notificationToken || '',
        lastSyncResult: buildSyncResult({
            success: true,
            message,
            notificationToken,
        }),
    });
};

const persistFailedSync = async ({ isEnabled, existingToken, message }) => {
    await persistPushNotificationState({
        isEnabled,
        tokenRegistered: false,
        lastToken: existingToken || '',
        lastSyncResult: buildSyncResult({
            success: false,
            message,
            notificationToken: existingToken || '',
        }),
    });
};

const getAuthRequirementError = () => {
    const error = new Error('Please log in before managing push notifications.');
    error.code = 'AUTH_REQUIRED';
    return error;
};

let syncInFlightPromise = null;

const runSerialSync = async (job) => {
    while (syncInFlightPromise) {
        try {
            await syncInFlightPromise;
        } catch (_error) {
            // Ignore previous sync errors, the next sync should still run.
        }
    }

    syncInFlightPromise = job();

    try {
        return await syncInFlightPromise;
    } finally {
        syncInFlightPromise = null;
    }
};

export async function syncNotificationTokenWithBackend(notificationToken, { markEnabled = true } = {}) {
    return runSerialSync(async () => {
        const accessToken = await getAccessToken();

        if (!accessToken) {
            throw getAuthRequirementError();
        }

        try {
            const payload = await buildTokenPayload(notificationToken);
            const response = await updateNotificationToken(payload);
            await persistSuccessfulSync({
                notificationToken,
                isEnabled: markEnabled && Boolean(notificationToken),
                message: notificationToken ? 'Push token synced successfully.' : 'Push token removed successfully.',
            });

            return response;
        } catch (error) {
            await persistFailedSync({
                isEnabled: markEnabled && Boolean(notificationToken),
                existingToken: notificationToken,
                message: error?.message || 'Push token sync failed.',
            });
            throw error;
        }
    });
}

export async function enablePushNotifications() {
    const permissionStatus = await requestAndroidNotificationPermission();

    if (permissionStatus !== PUSH_PERMISSION_STATUS.GRANTED) {
        await persistPushNotificationState({
            isEnabled: false,
            tokenRegistered: false,
            permissionStatus,
            lastSyncResult: buildSyncResult({
                success: false,
                message: permissionStatus === PUSH_PERMISSION_STATUS.BLOCKED
                    ? 'Notification permission is blocked in system settings.'
                    : 'Notification permission was not granted.',
            }),
        });

        return {
            permissionStatus,
            tokenRegistered: false,
            notificationToken: '',
        };
    }

    const token = await getFcmToken();

    if (!token) {
        const error = new Error('Failed to retrieve the Firebase push token.');
        await persistFailedSync({
            isEnabled: false,
            existingToken: '',
            message: error.message,
        });
        throw error;
    }

    await syncNotificationTokenWithBackend(token, { markEnabled: true });

    return {
        permissionStatus,
        tokenRegistered: true,
        notificationToken: token,
    };
}

export async function disablePushNotifications({ deleteToken = true, allowWithoutAuth = false } = {}) {
    return runSerialSync(async () => {
        const accessToken = await getAccessToken();

        if (!accessToken && !allowWithoutAuth) {
            throw getAuthRequirementError();
        }

        try {
            if (accessToken) {
                await updateNotificationToken(await buildTokenPayload(''));
            }

            if (deleteToken) {
                await deleteFcmToken();
            }

            await persistSuccessfulSync({
                notificationToken: '',
                isEnabled: false,
                message: 'Push notifications disabled on this device.',
            });

            return true;
        } catch (error) {
            await persistFailedSync({
                isEnabled: false,
                existingToken: '',
                message: error?.message || 'Push notification disable failed.',
            });
            throw error;
        }
    });
}

export async function bootstrapPushNotificationRegistration() {
    const [permissionStatus, accessToken, storedState] = await Promise.all([
        getNotificationPermissionStatus(),
        getAccessToken(),
        loadStoredPushNotificationState(),
    ]);

    if (!accessToken || permissionStatus !== PUSH_PERMISSION_STATUS.GRANTED) {
        return {
            synced: false,
            reason: !accessToken ? 'not-authenticated' : 'permission-not-granted',
        };
    }

    if (storedState.isEnabled === false) {
        return {
            synced: false,
            reason: 'disabled-locally',
        };
    }

    const token = await getFcmToken();

    if (!token) {
        await persistFailedSync({
            isEnabled: storedState.isEnabled !== false,
            existingToken: '',
            message: 'Failed to fetch the Firebase push token during bootstrap.',
        });
        return {
            synced: false,
            reason: 'missing-token',
        };
    }

    await syncNotificationTokenWithBackend(token, { markEnabled: true });

    return {
        synced: true,
        notificationToken: token,
    };
}

export async function syncRefreshedNotificationToken(notificationToken) {
    if (!notificationToken) {
        return {
            synced: false,
            reason: 'missing-token',
        };
    }

    const [storedState, permissionStatus, accessToken] = await Promise.all([
        loadStoredPushNotificationState(),
        getNotificationPermissionStatus(),
        getAccessToken(),
    ]);

    if (!accessToken) {
        return {
            synced: false,
            reason: 'not-authenticated',
        };
    }

    if (permissionStatus !== PUSH_PERMISSION_STATUS.GRANTED || storedState.isEnabled === false) {
        return {
            synced: false,
            reason: 'disabled',
        };
    }

    await syncNotificationTokenWithBackend(notificationToken, { markEnabled: true });

    return {
        synced: true,
        notificationToken,
    };
}

export async function unregisterPushNotificationsOnLogout() {
    const storedState = await loadStoredPushNotificationState();

    try {
        await updateNotificationToken(await buildTokenPayload(''));
        await persistPushNotificationState({
            tokenRegistered: false,
            lastSyncResult: buildSyncResult({
                success: true,
                message: 'Push token removed during logout.',
            }),
            lastToken: storedState.lastToken || '',
        });
    } catch (error) {
        await persistPushNotificationState({
            tokenRegistered: false,
            lastToken: storedState.lastToken || '',
            lastSyncResult: buildSyncResult({
                success: false,
                message: error?.message || 'Logout token removal failed.',
            }),
        });
    }
}



